import { Injectable, NgZone, inject } from '@angular/core';
import { BehaviorSubject, Observable, catchError, of, shareReplay, startWith, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { FirestoreReadyBoardDataSource } from './firestore-ready-board.data-source';
import { ReadyBoardSource, ReadyBoardState } from './ready-board.models';

const EMPTY_READY_BOARD_STATE: ReadyBoardState = {
  activePickups: [],
  announcements: [],
  schedule: [],
  tickerText: '',
  internalAnnouncements: [],
  presentChildren: []
};

@Injectable({ providedIn: 'root' })
export class ReadyBoardDataService {
  private readonly ngZone = inject(NgZone);
  private readonly firestoreEnabled = hasFirebaseConfig(environment);

  private readonly sourceSubject = new BehaviorSubject<ReadyBoardSource>('none');
  readonly source$ = this.sourceSubject.asObservable();

  private readonly lastKnownStateSubject = new BehaviorSubject<ReadyBoardState>(EMPTY_READY_BOARD_STATE);

  readonly readyBoardState$: Observable<ReadyBoardState> = this.firestoreEnabled
    ? this.createFirestoreStateStream()
    : of(EMPTY_READY_BOARD_STATE);

  private createFirestoreStateStream(): Observable<ReadyBoardState> {
    const firestoreSource = new FirestoreReadyBoardDataSource(this.ngZone);

    const firestore$ = firestoreSource.state$.pipe(
      tap(state => {
        this.lastKnownStateSubject.next(state);
        this.sourceSubject.next('firestore');
      }),
      catchError(error => {
        console.error('[ReadyBoardDataService] Firestore source error:', error);
        this.sourceSubject.next('none');
        return of(this.lastKnownStateSubject.value);
      })
    );

    return firestore$.pipe(
      startWith(EMPTY_READY_BOARD_STATE),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }
}

function hasFirebaseConfig(config: typeof environment): boolean {
  const firebase = config?.firebase;
  return Boolean(firebase && firebase.apiKey && firebase.projectId);
}
